import { Dictionary } from "./misc"

enum BankingPlatforms {
  finastra = 'finastra',
  banno = 'banno'
}

export type BankingPlatform = keyof typeof BankingPlatforms

export type CustomerConfig = {
  theme?: string               // should map to built themes
  // map of [valueslug]: sort order
  valuesSortOrder?: Dictionary<number>
  // map of [valueslug]: multiplier on the 10-point match basis. 1 is the default.
  valueWeights?: Dictionary<number>
  topTags?: string[]
}

export interface IImpactData {
  _id?: string
  customerId: string
  monthEnding: string
  data?: Dictionary<{
    quantity?: number
    amount?: number
  }>
}

export interface DataPoint {
  label: string
  value: string
  impactAreaId?: string
}

export interface Partnership {
  imgUrl: string
  imgHeight: number
  imgWidth: number
  link?: string
}

export const PARTNERSHIP_MAX_IMG_DIMS = {
  height: 70,
  width: 100
}

export type FinancialIntegration = {
  platform?: BankingPlatform

  // stable identifier for deep-links and per-integration routing
  integrationId?: string

  // finastra
  finastraTenantId?: string
  finastraClientId?: string
  finastraClientSecret?: string
  // finastra B2B
  finastraB2BTenant?: string
  finastraB2BClientId?: string
  finastraB2BClientSecret?: string

  // banno / jack henry authentication framework (per institution)
  bannoEnvironment?: string // e.g. https://digital.garden-fi.com
  bannoClientId?: string
  bannoClientSecret?: string
  /** OAuth redirect_uri origin (scheme+host[+port]). Empty = use incoming API request host. */
  bannoRedirectUrlBase?: string
}

export const RequestMethods = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE'
}

export type CustomRequest = {
  requestMethod: keyof typeof RequestMethods
  url: string
  headers: string
  body?: string
}

export type CustomerRequestSection = {
  message?: string
  request?: CustomRequest
}

export type CustomProduct = {
  name: string
  apy: number
  classCodes: number[]
  messaging: {
    recommendation: string
    requirementsToEarnHighInterest: string
    requirementsToKeepHighInterest: string
  }
}


export type PromotionItem = {
  image?: string
  name?: string
  description?: string
  url?: string
}

export type Promotion = {
  sectionTitle?: string
  promotionText?: string
  items?: PromotionItem[]
}


export interface Customer {
  _id: string
  name: string
  config?: CustomerConfig
  logo?: string
  integrations?: FinancialIntegration[]
  plaidInstitutionIDs?: string[]
  willNotFund?: Dictionary<boolean>
  highlightedDataPoints?: DataPoint[]
  partnerships?: Partnership[]
  partnershipHeader?: string
  partnershipSubtext?: string
  promotion?: Promotion | null
  featuredBusinesses?: {
    primary?: {
      title?: string
      bannerImage?: string
      featuredHeader?: string
      featuredSubtext?: string
      items?: Array<{
        id: string
        name: string
        description?: string
      }>
    }
    secondary?: {
      title?: string
      bannerImage?: string
      featuredHeader?: string
      featuredSubtext?: string
      items?: Array<{
        id: string
        name: string
        description?: string
      }>
    }
  }
  savingsImpact?: CustomerRequestSection | null
  businessReferrals?: CustomerRequestSection | null
  customProduct: CustomProduct
}

export const WillNotFund = {
  fossilFuel: {
    label: 'Fossil Fuels',
    icon: {
      family: 'MaterialIcons',
      name: 'local-gas-station'
    }
  },
  firearms: {
    label: 'Firearms and Ammunition',
    icon: {
      family: 'MaterialCommunityIcons',
      name: 'ammunition'
    }
  },
  privatePrisons: {
    label: 'Private Prisons',
    icon: {
      family: 'FontAwesome',
      name: 'building'
    }
  },
  tobacco: {
    label: 'Tobacco',
    icon: {
      family: 'MaterialCommunityIcons',
      name: 'smoking'
    }
  },
  paydayLenders: {
    label: 'Payday Lenders',
    icon: {
      family: 'Foundation',
      name: 'dollar-bill'
    }
  }
}

